import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Headphones, Plus, Mail, Send, X, Clock, AlertTriangle } from "lucide-react";
import { api, apiError } from "@/lib/api";

const STATUS_STYLE = {
  open: "bg-amber-100 text-amber-700", assigned: "bg-blue-100 text-blue-700",
  in_progress: "bg-indigo-100 text-indigo-700", waiting_customer: "bg-purple-100 text-purple-700",
  resolved: "bg-green-100 text-green-700", closed: "bg-slate-200 text-slate-600",
};
const PRIORITY = ["low", "medium", "high", "urgent"];

export function StatusChip({ status }) {
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize ${STATUS_STYLE[status] || "bg-slate-100 text-slate-600"}`} data-testid={`ticket-status-${status}`}>{(status || "").replace("_", " ")}</span>;
}

export default function SupportSelfService({ compact }) {
  const [cfg, setCfg] = useState({ email: "info@vs-solutions.org", categories: [] });
  const [tickets, setTickets] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ category: "", subject: "", message: "", priority: "medium" });
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");

  const load = () => api.get("/support/tickets").then((r) => setTickets(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/support/config").then((r) => { setCfg(r.data); setForm((f) => ({ ...f, category: r.data.categories[0] })); }).catch(() => {});
    load();
  }, []);

  const openTicket = async (id) => { try { const { data } = await api.get(`/support/tickets/${id}`); setActive(data); } catch (e) { toast.error(apiError(e)); } };

  const submit = async () => {
    if (!form.message.trim()) return toast.error("Describe your issue");
    try { await api.post("/support/tickets", form); toast.success("Ticket raised — confirmation emailed"); setCreating(false); setForm((f) => ({ ...f, subject: "", message: "" })); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const sendReply = async () => {
    if (!reply.trim()) return;
    try { await api.post(`/support/tickets/${active.id}/reply`, { text: reply }); setReply(""); openTicket(active.id); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const closeTicket = async () => { await api.post(`/support/tickets/${active.id}/close`); toast.success("Ticket closed"); setActive(null); load(); };

  return (
    <div className="space-y-3">
      <div className="bg-gradient-to-r from-coral to-coral-dark text-white rounded-2xl p-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3"><Headphones size={24} /><div><div className="font-bold">BiteGoFood Support</div><div className="text-xs text-white/85">{cfg.email}</div></div></div>
        <a href={`mailto:${cfg.email}`} className="bg-white/20 hover:bg-white/30 rounded-xl px-3 py-2 text-sm font-semibold flex items-center gap-1.5 tap" data-testid="email-support-btn"><Mail size={15} /> Email</a>
      </div>

      <div className="bg-white rounded-2xl border border-border p-4">
        <div className="flex items-center justify-between mb-3"><h3 className="font-bold text-ink">Your tickets</h3><button onClick={() => setCreating(!creating)} className="text-coral font-semibold text-sm flex items-center gap-1" data-testid="new-ticket-btn"><Plus size={15} /> New ticket</button></div>
        {creating && (
          <div className="bg-surface rounded-xl p-3 mb-3 space-y-2" data-testid="new-ticket-form">
            <div className="flex gap-2 overflow-x-auto hide-scrollbar">{cfg.categories.map((c) => <button key={c} onClick={() => setForm((f) => ({ ...f, category: c }))} className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap border ${form.category === c ? "bg-coral text-white border-coral" : "bg-white border-border"}`}>{c}</button>)}</div>
            <input value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} placeholder="Subject (optional)" className="w-full bg-white border border-border rounded-lg px-3 py-2 text-sm outline-none" data-testid="ticket-subject" />
            <textarea value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} placeholder="Describe your issue..." rows={3} className="w-full bg-white border border-border rounded-lg px-3 py-2 text-sm outline-none" data-testid="ticket-message" />
            <div className="flex items-center gap-2">
              <span className="text-xs text-ink-600">Priority</span>
              <select value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} className="bg-white border border-border rounded-lg px-2 py-1.5 text-sm outline-none capitalize" data-testid="ticket-priority">{PRIORITY.map((p) => <option key={p} value={p}>{p}</option>)}</select>
              <button onClick={submit} className="ml-auto bg-coral text-white font-bold py-2 px-4 rounded-lg text-sm tap" data-testid="submit-ticket">Raise ticket</button>
            </div>
          </div>
        )}
        {tickets.length === 0 ? <p className="text-sm text-ink-600 py-3">No tickets yet.</p> : (
          <div className="divide-y divide-border">
            {tickets.map((t) => (
              <button key={t.id} onClick={() => openTicket(t.id)} className="w-full text-left py-2.5 flex items-start justify-between gap-2" data-testid={`ticket-row-${t.code}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2"><span className="font-semibold text-sm truncate">{t.subject || t.category || t.topic}</span>{t.sla?.breached && <AlertTriangle size={13} className="text-bad shrink-0" />}</div>
                  <div className="text-xs text-ink-600 truncate">{t.code} · {t.category || t.topic}</div>
                </div>
                <StatusChip status={t.status} />
              </button>
            ))}
          </div>
        )}
      </div>

      {active && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setActive(null)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="ticket-detail">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div><div className="font-bold text-ink">{active.subject || active.category}</div><div className="text-xs text-ink-600">{active.code} · {active.category}</div></div>
              <div className="flex items-center gap-2"><StatusChip status={active.status} /><button onClick={() => setActive(null)} data-testid="close-ticket-detail"><X size={18} /></button></div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {active.sla && <div className="flex items-center gap-1.5 text-[11px] text-ink-600"><Clock size={12} /> Resolution by {active.sla.resolution_due?.slice(0, 16).replace("T", " ")}{active.sla.breached && <span className="text-bad font-bold ml-1">SLA breached</span>}</div>}
              {active.messages?.map((m, i) => (
                <div key={i} className={`flex ${m.from === "you" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.from === "you" ? "bg-coral text-white" : "bg-surface text-ink"}`}>{m.text}<div className={`text-[9px] mt-1 ${m.from === "you" ? "text-white/70" : "text-ink-600"}`}>{m.at?.slice(0, 16).replace("T", " ")}</div></div>
                </div>
              ))}
            </div>
            {active.status !== "closed" && (
              <div className="p-3 border-t border-border">
                <div className="flex gap-2">
                  <input value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply()} placeholder="Type a reply..." className="flex-1 bg-surface rounded-xl px-3 py-2.5 text-sm outline-none" data-testid="ticket-reply-input" />
                  <button onClick={sendReply} className="w-11 rounded-xl bg-coral text-white flex items-center justify-center tap" data-testid="ticket-reply-send"><Send size={17} /></button>
                </div>
                <button onClick={closeTicket} className="mt-2 text-xs text-ink-600 font-semibold" data-testid="ticket-close-btn">Mark as closed</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
